import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Drawer } from '../../theme/components';
import SaleHistoryTimeline from '../sales/modals/SaleHistoryTimeline';
import UserDetailsModal from './UserDetailsModal';
import { API_URL } from '../../config/api';
import CurrencyFormatter from '../../utils/CurrencyFormatter';

/**
 * 🎯 AdminOrderDrawer - Drawer Completo de Detalhes do Pedido para Admin
 * 
 * Features:
 * - Todos os dados da transação
 * - Histórico completo de movimentação
 * - Links para produtor, cliente e afiliado
 * - Ações administrativas
 */
const AdminOrderDrawer = ({
  show,
  onHide,
  orderId,
  order,
  onOrderUpdated
}) => {
  const [orderData, setOrderData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);

  // Carregar dados do pedido
  useEffect(() => {
    if (show && orderId) {
      loadOrderDetails();
      loadSaleHistory(1, true);
    }
  }, [show, orderId]);

  /**
   * 📊 Carregar detalhes completos do pedido
   */
  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar detalhes do pedido');
      }

      const data = await response.json();
      setOrderData(data.data || order);
    } catch (err) {
      console.error('❌ Erro ao carregar pedido:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 📜 Carregar histórico da venda (paginado)
   */
  const loadSaleHistory = async (page = 1, reset = false) => {
    setHistoryLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_URL}/api/sales/${orderId}/history?page=${page}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Erro ao carregar histórico');
      }

      const historyData = responseData.data || {};
      const newHistory = historyData.data || [];
      const pagination = historyData.pagination || { currentPage: 1, totalPages: 1 };
      
      if (reset) {
        setHistory(newHistory);
      } else {
        setHistory(prev => [...prev, ...newHistory]);
      }

      setHistoryPage(page);
      setHasMoreHistory(pagination.currentPage < pagination.totalPages);
    } catch (err) {
      console.error('❌ Erro ao carregar histórico:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * 🔄 Carregar mais histórico (scroll infinito)
   */
  const handleLoadMoreHistory = () => {
    if (!historyLoading && hasMoreHistory) {
      loadSaleHistory(historyPage + 1, false);
    }
  };

  /**
   * ✅ Callback após ação bem-sucedida
   */
  const handleActionSuccess = () => {
    loadOrderDetails();
    loadSaleHistory(1, true);
    if (onOrderUpdated) {
      onOrderUpdated();
    }
  };

  /**
   * 📅 Formatar data
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * 💰 Formatar preço
   */
  const formatPrice = (priceInCents) => {
    return CurrencyFormatter.centsToFormatted(priceInCents);
  };

  /**
   * 🎨 Badge de status
   */
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_payment': { color: 'warning', icon: 'solar:clock-circle-bold', text: 'Aguardando Pagamento' },
      'paid': { color: 'info', icon: 'solar:check-circle-bold', text: 'Pago' },
      'processing': { color: 'primary', icon: 'solar:settings-bold', text: 'Em Preparação' },
      'shipped': { color: 'success', icon: 'solar:delivery-bold', text: 'Em Rota' },
      'awaiting_pickup': { color: 'warning', icon: 'solar:box-bold', text: 'Aguardando Retirada' },
      'delivered': { color: 'success', icon: 'solar:home-2-bold', text: 'Entregue' },
      'cancelled': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Cancelado' },
      'failed': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Frustrado' }
    };

    const config = statusConfig[status] || statusConfig['pending_payment'];
    
    return (
      <span className={`badge bg-${config.color} text-white d-flex align-items-center gap-1`}>
        <Icon icon={config.icon} className="text-xs" />
        {config.text}
      </span>
    );
  };

  /**
   * 💳 Ícone do método de pagamento
   */
  const getPaymentMethodIcon = (method) => {
    const methods = {
      'credit_card': 'mdi:credit-card',
      'debit_card': 'mdi:credit-card-outline',
      'pix': 'mdi:qrcode',
      'boleto': 'mdi:file-document',
      'after_pay': 'mdi:truck-delivery'
    };
    return methods[method] || 'mdi:credit-card';
  };

  /**
   * 💳 Texto do método de pagamento
   */
  const getPaymentMethodText = (method) => {
    const methods = {
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'boleto': 'Boleto',
      'after_pay': 'Pagamento Após Entrega'
    };
    return methods[method] || method;
  };

  /**
   * 🔗 Abrir modal de detalhes do usuário
   */
  const openUserModal = (userId, type) => {
    setSelectedUserId(userId);
    setSelectedUserType(type);
    setShowUserModal(true);
  };

  /**
   * 🎨 Determinar status do AfterPay (mesma lógica do SaleDetailsModal)
   */
  const getAfterPayStatus = () => {
    if (!data || !data.is_afterpay) return null;

    const { payment_status, tracking_code, delivered_at, metadata } = data;

    // Helper: Calcular dias desde a entrega
    const getDaysSinceDelivery = (deliveredDate) => {
      if (!deliveredDate) return null;
      const now = new Date();
      const delivered = new Date(deliveredDate);
      const diffTime = Math.abs(now - delivered);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    // Parseando metadata se for string
    let parsedMetadata = {};
    if (metadata) {
      try {
        parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      } catch (e) {
        parsedMetadata = {};
      }
    }

    const validationErrors = parsedMetadata.validation_errors;
    const trackingStatus = parsedMetadata.tracking_status;
    const attentionPriority = parseInt(parsedMetadata.attention_priority) || 0;

    // 🎯 LÓGICA EXATA DO KANBAN (ordem importa!)
    
    // 1. REPORTADOS: Tem erros de validação
    if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
      return { status: 'Reportado', color: 'warning', icon: 'solar:danger-triangle-bold' };
    }
    
    // 2. EXPEDIÇÃO: Sem código de rastreio (DEFAULT)
    if (!tracking_code) {
      return { status: 'Em Expedição', color: 'info', icon: 'solar:box-bold' };
    }
    
    // 3. ENVIADOS: Tem código de rastreio mas não saiu para entrega
    if (tracking_code && 
        (!trackingStatus || 
         (trackingStatus !== 'out_for_delivery' && trackingStatus !== 'available_for_pickup'))) {
      return { status: 'Enviado', color: 'primary', icon: 'solar:delivery-bold' };
    }
    
    // 4. SAIU PARA ENTREGA: Status específico
    if (trackingStatus === 'out_for_delivery') {
      return { status: 'Saiu para Entrega', color: 'primary', icon: 'solar:truck-bold' };
    }
    
    // 5. RETIRAR NOS CORREIOS: Disponível para retirada
    if (trackingStatus === 'available_for_pickup') {
      return { status: 'Aguardando Retirada', color: 'secondary', icon: 'solar:box-bold' };
    }
    
    // 6. REQUER ATENÇÃO: Tem prioridade definida
    if (attentionPriority > 0) {
      return { status: 'Requer Atenção', color: 'warning', icon: 'solar:danger-triangle-bold' };
    }
    
    // 7. ENTREGUE: Entregue mas não pago (0-2 dias)
    if (delivered_at && payment_status !== 'paid') {
      const daysSince = getDaysSinceDelivery(delivered_at);
      if (daysSince !== null && daysSince < 3) {
        return { status: 'Entregue', color: 'success', icon: 'solar:home-2-bold' };
      }
    }
    
    // 8. INADIMPLÊNCIA: Entregue há 3-5 dias sem pagamento
    if (delivered_at && payment_status !== 'paid') {
      const daysSince = getDaysSinceDelivery(delivered_at);
      if (daysSince !== null && daysSince >= 3 && daysSince <= 5) {
        return { status: 'Inadimplente', color: 'danger', icon: 'solar:close-circle-bold' };
      }
    }
    
    // 9. FRUSTRADOS: Entregue há 7+ dias sem pagamento
    if (delivered_at && payment_status !== 'paid') {
      const daysSince = getDaysSinceDelivery(delivered_at);
      if (daysSince !== null && daysSince > 6) {
        return { status: 'Frustrado', color: 'danger', icon: 'solar:close-circle-bold' };
      }
    }
    
    // 10. PAGOS: Produto entregue E pago
    if (delivered_at && payment_status === 'paid') {
      return { status: 'Pago', color: 'success', icon: 'solar:check-circle-bold' };
    }

    return { status: 'Em Expedição', color: 'info', icon: 'solar:box-bold' };
  };

  if (!orderData && !order) return null;

  const data = orderData || order;

  return (
    <Drawer
      show={show}
      onHide={onHide}
      title={`Pedido #${data.id}`}
      icon="solar:file-text-bold"
      size="xl"
    >
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <p className="mt-2">Carregando detalhes...</p>
        </div>
      ) : (
        <div style={{ padding: '0 4px' }}>
          {/* Informações Gerais do Pedido */}
          <Drawer.Section title="Informações do Pedido" icon="solar:file-text-bold">
            <div className="row g-3">
              <div className="col-md-6">
                <Drawer.Row label="ID do Pedido" value={`#${data.id}`} highlight />
              </div>
              <div className="col-md-6">
                <Drawer.Row label="Status" value={getStatusBadge(data.sale_status)} />
              </div>
              <div className="col-md-6">
                <Drawer.Row label="Valor Total" value={formatPrice(data.total_amount)} highlight />
              </div>
              <div className="col-md-6">
                <Drawer.Row label="Data do Pedido" value={formatDate(data.created_at)} />
              </div>
              <div className="col-md-6">
                <Drawer.Row 
                  label="Forma de Pagamento" 
                  value={
                    <span className="d-flex align-items-center gap-2">
                      <Icon icon={getPaymentMethodIcon(data.payment_method)} />
                      {getPaymentMethodText(data.payment_method)}
                    </span>
                  } 
                />
              </div>
              <div className="col-md-6">
                <Drawer.Row label="Data do Pagamento" value={formatDate(data.payment_date)} />
              </div>
            </div>

            {data.product_name && (
              <>
                <Drawer.Divider />
                <div className="row g-3">
                  <div className="col-12">
                    <Drawer.Row label="Produto" value={data.product_name} highlight />
                  </div>
                  {data.product_description && (
                    <div className="col-12">
                      <small className="text-muted">{data.product_description}</small>
                    </div>
                  )}
                </div>
              </>
            )}
          </Drawer.Section>

          <Drawer.Divider />

          {/* Dados do Cliente */}
          {data.customer_id && (
            <>
              <Drawer.Section title="Dados do Cliente" icon="solar:user-bold">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Drawer.Row label="Nome" value={data.customer_name || '-'} />
                  </div>
                  <div className="col-md-6">
                    <Drawer.Row label="Email" value={data.customer_email || '-'} />
                  </div>
                  {data.customer_phone && (
                    <div className="col-md-6">
                      <Drawer.Row label="Telefone" value={data.customer_phone} />
                    </div>
                  )}
                  {data.customer_document && (
                    <div className="col-md-6">
                      <Drawer.Row label="CPF/CNPJ" value={data.customer_document} />
                    </div>
                  )}
                  <div className="col-12">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => openUserModal(data.customer_id, 'customer')}
                    >
                      <Icon icon="solar:eye-bold" className="me-2" />
                      Ver Detalhes Completos
                    </button>
                  </div>
                </div>
              </Drawer.Section>

              <Drawer.Divider />
            </>
          )}

          {/* Dados do Produtor */}
          {data.producer_id && (
            <>
              <Drawer.Section title="Dados do Produtor" icon="solar:shop-bold">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Drawer.Row label="Nome" value={data.producer_name || '-'} />
                  </div>
                  <div className="col-md-6">
                    <Drawer.Row label="Email" value={data.producer_email || '-'} />
                  </div>
                  {data.producer_phone && (
                    <div className="col-md-6">
                      <Drawer.Row label="Telefone" value={data.producer_phone} />
                    </div>
                  )}
                  <div className="col-12">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => openUserModal(data.producer_id, 'producer')}
                    >
                      <Icon icon="solar:eye-bold" className="me-2" />
                      Ver Detalhes Completos
                    </button>
                  </div>
                </div>
              </Drawer.Section>

              <Drawer.Divider />
            </>
          )}

          {/* Dados do Afiliado */}
          {data.affiliate_id && (
            <>
              <Drawer.Section title="Dados do Afiliado" icon="solar:users-group-rounded-bold">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Drawer.Row label="Nome" value={data.affiliate_name || '-'} />
                  </div>
                  <div className="col-md-6">
                    <Drawer.Row label="Email" value={data.affiliate_email || '-'} />
                  </div>
                  {data.affiliate_commission > 0 && (
                    <div className="col-md-6">
                      <Drawer.Row 
                        label="Comissão" 
                        value={formatPrice(data.affiliate_commission)} 
                        highlight 
                      />
                    </div>
                  )}
                  <div className="col-12">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => openUserModal(data.affiliate_id, 'affiliate')}
                    >
                      <Icon icon="solar:eye-bold" className="me-2" />
                      Ver Detalhes Completos
                    </button>
                  </div>
                </div>
              </Drawer.Section>

              <Drawer.Divider />
            </>
          )}

          {/* Comissionamento */}
          {data.commission_details && data.commission_details.length > 0 && (
            <>
              <Drawer.Section title="Comissionamento" icon="solar:wallet-money-bold">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Nome</th>
                        <th>% Comissão</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.commission_details.map((commission, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`badge ${
                              commission.role_type === 'affiliate' ? 'bg-info' :
                              commission.role_type === 'coproducer' ? 'bg-warning' : 'bg-success'
                            }`}>
                              {commission.role_type === 'affiliate' ? 'Afiliado' :
                               commission.role_type === 'coproducer' ? 'Coprodutor' : 'Gerente'}
                            </span>
                          </td>
                          <td className="fw-medium">{commission.role_name}</td>
                          <td>{commission.commission_rate}%</td>
                          <td className="text-success fw-bold">
                            {formatPrice(commission.commission_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-active">
                        <td colSpan="3" className="fw-bold">Total:</td>
                        <td className="fw-bold text-success">
                          {formatPrice(data.total_commission || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Drawer.Section>

              <Drawer.Divider />
            </>
          )}

          {/* Endereço de Entrega */}
          {data.shipping_address && (
            <>
              <Drawer.Section title="Endereço de Entrega" icon="solar:map-point-bold">
                {(() => {
                  try {
                    const address = typeof data.shipping_address === 'string' 
                      ? JSON.parse(data.shipping_address)
                      : data.shipping_address;
                    
                    return (
                      <div className="bg-light p-3 radius-8">
                        <p className="mb-1 fw-medium">{address.recipient || address.street}</p>
                        <p className="mb-1">{address.street} {address.number}</p>
                        {address.complement && <p className="mb-1">{address.complement}</p>}
                        <p className="mb-1">{address.neighborhood}, {address.city} - {address.state}</p>
                        <p className="mb-0">CEP: {address.zipCode || address.zip}</p>
                      </div>
                    );
                  } catch {
                    return <p className="mb-0 text-muted">Endereço não disponível</p>;
                  }
                })()}
              </Drawer.Section>

              <Drawer.Divider />
            </>
          )}

          {/* AfterPay Status */}
          {data.is_afterpay && (
            <>
              <Drawer.Section title="Status AfterPay" icon="solar:truck-delivery-bold">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Drawer.Row 
                      label="Tipo de Pedido" 
                      value={
                        <span className="badge bg-danger text-white">
                          <Icon icon="solar:truck-delivery-bold" className="me-1" />
                          AfterPay
                        </span>
                      } 
                      highlight 
                    />
                  </div>
                  {(() => {
                    const afterPayStatus = getAfterPayStatus();
                    if (afterPayStatus) {
                      return (
                        <div className="col-md-6">
                          <Drawer.Row 
                            label="Status AfterPay" 
                            value={
                              <span className={`badge bg-${afterPayStatus.color} text-white d-flex align-items-center gap-1`}>
                                <Icon icon={afterPayStatus.icon} className="text-xs" />
                                {afterPayStatus.status}
                              </span>
                            } 
                            highlight 
                          />
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </Drawer.Section>

              <Drawer.Divider />
            </>
          )}

          {/* Rastreamento */}
          {data.tracking_code && (
            <>
              <Drawer.Section title="Rastreamento" icon="solar:delivery-bold">
                <Drawer.Row label="Código de Rastreio" value={data.tracking_code} highlight />
                {data.shipping_company && (
                  <Drawer.Row label="Transportadora" value={data.shipping_company} />
                )}
              </Drawer.Section>

              <Drawer.Divider />
            </>
          )}

          {/* Histórico de Movimentação */}
          <Drawer.Section title="Histórico de Movimentação" icon="solar:history-bold">
            <SaleHistoryTimeline
              saleId={orderId}
              history={history}
              loading={historyLoading}
              hasMore={hasMoreHistory}
              onLoadMore={handleLoadMoreHistory}
              onActionSuccess={handleActionSuccess}
            />
          </Drawer.Section>
        </div>
      )}

      {/* Modal de Detalhes do Usuário */}
      <UserDetailsModal
        show={showUserModal}
        onHide={() => {
          setShowUserModal(false);
          setSelectedUserId(null);
          setSelectedUserType(null);
        }}
        userId={selectedUserId}
        userType={selectedUserType}
      />
    </Drawer>
  );
};

export default AdminOrderDrawer;

